/**
 * Hand-authored pixel-art sprite atlas, baked to canvases at load time.
 *
 * Every sprite is built procedurally from a tiny 2D-grid toolkit (rects,
 * circles, ellipses, diamonds) rather than raster files — the game stays a
 * dependency-free, single-bundle deploy. Each grid is finished with
 * `autoOutline`, which draws a 1px dark outline around every shape's outer
 * silhouette (any transparent pixel touching a colored one), so authors never
 * have to hand-place border pixels.
 *
 * Team-owned sprites (units, buildings) are authored once per shape using
 * placeholder chars 'T' / 't' / 'D' (team base / highlight / shadow) and
 * baked once per player color at startup. Neutral sprites (data nodes,
 * markets) use fixed palette chars only and are baked once.
 *
 * Animation: every sprite can have 1-6 FRAMES (minimal pixel deltas from the
 * base — readable art over frame count). Frames are baked lazily per
 * type/owner/frame(/flip) the same way the static atlas was, so steady-state
 * rendering never creates a canvas. `frameIndexFor` picks a frame from wall
 * clock time offset by entity id, so identical units visibly de-sync.
 */

import type { BuildingType, UnitType } from '../sim';

export const PLAYER_COLORS = ['#4f8ef7', '#e5484d', '#46a758', '#8e4ec6'];
const NEUTRAL_TEAM_COLOR = '#8b98ad';

// ---------------------------------------------------------------------------
// Color helpers
// ---------------------------------------------------------------------------

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  const c = (x: number) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

function lighten(hex: string, amt: number): string {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(r + (255 - r) * amt, g + (255 - g) * amt, b + (255 - b) * amt);
}

function darken(hex: string, amt: number): string {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(r * (1 - amt), g * (1 - amt), b * (1 - amt));
}

function teamPalette(base: string): Record<string, string> {
  return { T: base, t: lighten(base, 0.42), D: darken(base, 0.48) };
}

/** Shared neutral palette chars, available in every sprite. */
const NEUTRAL: Record<string, string> = {
  '0': '#0a0e14', // outline
  A: '#c9d3e0', // light metal
  B: '#7c8796', // mid metal
  C: '#414a58', // dark metal
  K: '#070a10', // near-black glass/visor
  S: '#7dd3fc', // cyan glow
  G: '#4fe68a', // bright green glow / LED
  g: '#1f6b45', // dim green
  Y: '#eab308', // hazard gold
  R: '#ff5c5c', // red glow / warning
  W: '#eafffb', // white glow
  M: '#7a2020', // maroon accent
  Q: '#2dd4bf', // teal (data)
  q: '#7ce8d9', // light teal (data)
};

// ---------------------------------------------------------------------------
// Tiny pixel-grid authoring toolkit
// ---------------------------------------------------------------------------

type Grid = string[][];

function makeGrid(w: number, h: number): Grid {
  return Array.from({ length: h }, () => Array.from({ length: w }, () => '.'));
}

function inBounds(g: Grid, x: number, y: number): boolean {
  return y >= 0 && y < g.length && x >= 0 && x < g[0].length;
}

function px1(g: Grid, x: number, y: number, ch: string): void {
  if (inBounds(g, x, y)) g[y][x] = ch;
}

function rect(g: Grid, x: number, y: number, w: number, h: number, ch: string): void {
  for (let j = y; j < y + h; j++) for (let i = x; i < x + w; i++) if (inBounds(g, i, j)) g[j][i] = ch;
}

function circle(g: Grid, cx: number, cy: number, r: number, ch: string): void {
  const r2 = r * r;
  for (let j = 0; j < g.length; j++) {
    for (let i = 0; i < g[0].length; i++) {
      const dx = i - cx + 0.5;
      const dy = j - cy + 0.5;
      if (dx * dx + dy * dy <= r2) g[j][i] = ch;
    }
  }
}

function ellipse(g: Grid, cx: number, cy: number, rx: number, ry: number, ch: string): void {
  for (let j = 0; j < g.length; j++) {
    for (let i = 0; i < g[0].length; i++) {
      const dx = (i - cx + 0.5) / rx;
      const dy = (j - cy + 0.5) / ry;
      if (dx * dx + dy * dy <= 1) g[j][i] = ch;
    }
  }
}

function diamond(g: Grid, cx: number, cy: number, rx: number, ry: number, ch: string): void {
  for (let j = 0; j < g.length; j++) {
    for (let i = 0; i < g[0].length; i++) {
      const dx = Math.abs(i - cx + 0.5) / rx;
      const dy = Math.abs(j - cy + 0.5) / ry;
      if (dx + dy <= 1) g[j][i] = ch;
    }
  }
}

/** Punch a hole (set back to transparent) — combine with circle/ellipse for rings. */
function cut(g: Grid, cx: number, cy: number, r: number): void {
  circle(g, cx, cy, r, '.');
}

/** Draw a 1px outline around every shape's outer silhouette (vs transparent bg). */
function autoOutline(g: Grid): void {
  const h = g.length;
  const w = g[0].length;
  const orig = g.map((row) => row.slice());
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (orig[y][x] !== '.') continue;
      const neighbors: Array<[number, number]> = [
        [x - 1, y],
        [x + 1, y],
        [x, y - 1],
        [x, y + 1],
      ];
      for (const [nx, ny] of neighbors) {
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const nv = orig[ny][nx];
        if (nv !== '.' && nv !== '0') {
          g[y][x] = '0';
          break;
        }
      }
    }
  }
}

function finalize(g: Grid): string[] {
  return g.map((row) => row.join(''));
}

// ---------------------------------------------------------------------------
// Baking: grid + palette -> canvas
// ---------------------------------------------------------------------------

function bake(grid: string[], palette: Record<string, string>): HTMLCanvasElement {
  const h = grid.length;
  const w = grid[0].length;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  for (let y = 0; y < h; y++) {
    const row = grid[y];
    if (row.length !== w) throw new Error(`sprite row ${y} has width ${row.length}, expected ${w}`);
    for (let x = 0; x < w; x++) {
      const ch = row[x];
      if (ch === '.') continue;
      ctx.fillStyle = palette[ch] ?? '#ff00ff';
      ctx.fillRect(x, y, 1, 1);
    }
  }
  return canvas;
}

/** Solid-color silhouette of a baked sprite, masked by its alpha channel — used for placement ghosts. */
function silhouette(source: HTMLCanvasElement, color: string): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = source.height;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(source, 0, 0);
  return canvas;
}

/** Horizontally-mirrored copy of a baked sprite — baked once and cached, never per-frame. */
function mirrorH(source: HTMLCanvasElement): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = source.height;
  const ctx = canvas.getContext('2d')!;
  ctx.translate(canvas.width, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(source, 0, 0);
  return canvas;
}

// ---------------------------------------------------------------------------
// Unit sprites (16x16)
// ---------------------------------------------------------------------------

const U = 16;

function buildEngineerFrames(): string[][] {
  function frame(walkPhase: 0 | 1): string[] {
    const g = makeGrid(U, U);
    // feet — alternate stride between the two phases (minimal delta)
    if (walkPhase === 0) {
      rect(g, 5, 13, 2, 2, 'C');
      rect(g, 9, 13, 2, 2, 'C');
    } else {
      rect(g, 5, 14, 2, 1, 'C');
      rect(g, 9, 12, 2, 3, 'C');
    }
    // torso
    rect(g, 4, 8, 8, 5, 'T');
    rect(g, 5, 9, 2, 1, 't');
    rect(g, 9, 11, 2, 2, 'D');
    // shoulder band
    rect(g, 5, 7, 6, 1, 'B');
    // head + visor
    rect(g, 6, 4, 4, 3, 'K');
    rect(g, 7, 5, 2, 1, 'S');
    // hard hat
    rect(g, 5, 3, 6, 1, 'Y');
    rect(g, 6, 1, 4, 2, 'Y');
    // wrench arm
    rect(g, 11, 9, 3, 2, 'B');
    px1(g, 13, 8, 'B');
    autoOutline(g);
    return finalize(g);
  }
  return [frame(0), frame(1)];
}

function buildInferenceAgentFrames(): string[][] {
  function frame(antennaOn: boolean): string[] {
    const g = makeGrid(U, U);
    // antenna — blinks between lit and dim
    rect(g, 8, 1, 1, 2, 'B');
    px1(g, 8, 0, antennaOn ? 'W' : 'B');
    // orb body
    circle(g, 8, 8, 5, 'T');
    circle(g, 6, 6, 1, 't');
    // visor / chat glow
    circle(g, 8, 9, 2, 'K');
    circle(g, 8, 9, 1, 'S');
    // thruster glow underneath (floating)
    rect(g, 6, 13, 4, 1, 'G');
    autoOutline(g);
    return finalize(g);
  }
  return [frame(true), frame(false)];
}

function buildScoutCrawlerFrames(): string[][] {
  function frame(phase: 0 | 1): string[] {
    const g = makeGrid(U, U);
    // legs — front/back pairs shift a pixel to suggest a skitter
    if (phase === 0) {
      rect(g, 1, 4, 5, 1, 'C');
      rect(g, 1, 11, 5, 1, 'C');
      rect(g, 10, 4, 5, 1, 'C');
      rect(g, 10, 11, 5, 1, 'C');
      px1(g, 2, 3, 'C');
      px1(g, 2, 12, 'C');
      px1(g, 13, 3, 'C');
      px1(g, 13, 12, 'C');
    } else {
      rect(g, 0, 4, 5, 1, 'C');
      rect(g, 2, 11, 5, 1, 'C');
      rect(g, 11, 4, 5, 1, 'C');
      rect(g, 9, 11, 5, 1, 'C');
      px1(g, 1, 3, 'C');
      px1(g, 4, 12, 'C');
      px1(g, 14, 3, 'C');
      px1(g, 11, 12, 'C');
    }
    // flat body
    rect(g, 6, 6, 4, 4, 'T');
    circle(g, 8, 7, 1, 'W');
    px1(g, 8, 7, 'S');
    autoOutline(g);
    return finalize(g);
  }
  return [frame(0), frame(1)];
}

function buildRedTeamAgentFrames(): string[][] {
  function frame(glow: boolean): string[] {
    const g = makeGrid(U, U);
    diamond(g, 8, 7, 4, 4, 'T');
    diamond(g, 8, 7, 2, 2, 'D');
    circle(g, 8, 7, 1, glow ? 'W' : 'R');
    // spike tips beyond the diamond points — bleed glow color when hot
    px1(g, 8, 2, glow ? 'R' : 'C');
    px1(g, 8, 12, glow ? 'R' : 'C');
    px1(g, 3, 7, 'C');
    px1(g, 13, 7, 'C');
    // legs/feet
    rect(g, 6, 12, 1, 2, 'C');
    rect(g, 9, 12, 1, 2, 'C');
    autoOutline(g);
    return finalize(g);
  }
  return [frame(false), frame(true)];
}

function buildGuardrailSentinelFrames(): string[][] {
  function frame(glint: boolean): string[] {
    const g = makeGrid(U, U);
    // legs
    rect(g, 6, 13, 2, 2, 'C');
    rect(g, 9, 13, 2, 2, 'C');
    // torso
    rect(g, 5, 8, 7, 5, 'T');
    rect(g, 9, 9, 2, 1, 't');
    // head
    rect(g, 7, 5, 3, 3, 'B');
    rect(g, 7, 6, 3, 1, 'S');
    // shield, with a team-color boss so ownership still reads
    rect(g, 1, 6, 4, 8, 'A');
    rect(g, 1, 6, 4, 1, 'S');
    circle(g, 3, 10, 2, 'T');
    circle(g, 3, 10, 1, 't');
    if (glint) {
      px1(g, 2, 8, 'W');
      px1(g, 3, 7, 'W');
    }
    autoOutline(g);
    return finalize(g);
  }
  // 3 quiet frames, 1 glint frame — reads as an occasional shine, not a strobe
  return [frame(false), frame(false), frame(false), frame(true)];
}

function buildFrontierAgentFrames(): string[][] {
  function frame(stage: 0 | 1 | 2): string[] {
    const g = makeGrid(U, U);
    // legs
    rect(g, 4, 13, 3, 2, 'C');
    rect(g, 9, 13, 3, 2, 'C');
    // lower torso — dark chassis so the team-color shoulders and glowing core both pop
    rect(g, 4, 9, 8, 4, 'C');
    // glowing core — pulses through 3 stages
    if (stage === 0) {
      circle(g, 8, 10, 2, 'W');
      circle(g, 8, 10, 1, 'S');
    } else if (stage === 1) {
      circle(g, 8, 10, 2, 'S');
      circle(g, 8, 10, 1, 'W');
    } else {
      circle(g, 8, 10, 3, 'W');
      circle(g, 8, 10, 1, 'S');
    }
    // broad shoulders
    rect(g, 2, 6, 12, 3, 'T');
    rect(g, 2, 6, 3, 1, 't');
    rect(g, 11, 8, 3, 1, 'D');
    // head
    rect(g, 6, 3, 4, 3, 'B');
    rect(g, 7, 4, 2, 1, 'R');
    autoOutline(g);
    return finalize(g);
  }
  return [frame(0), frame(1), frame(2)];
}

function buildLoadTesterFrames(): string[][] {
  // Siege engine: wide tracked base, rear counterweight box, long launcher
  // arm angled up over the chassis. Frame 2 ("recoil") swings the arm down
  // and shows a muzzle flash where the payload just left.
  function frame(recoil: boolean): string[] {
    const g = makeGrid(U, U);
    // wide tracked base
    rect(g, 1, 13, 14, 2, 'C');
    px1(g, 3, 14, 'B');
    px1(g, 7, 14, 'B');
    px1(g, 11, 14, 'B');
    // squat chassis, team-color
    rect(g, 3, 9, 9, 4, 'T');
    rect(g, 4, 10, 3, 1, 't');
    rect(g, 9, 11, 2, 2, 'D');
    // rear counterweight box anchoring the arm's pivot
    rect(g, 10, 6, 3, 3, 'C');
    px1(g, 11, 6, 'B');
    if (!recoil) {
      // arm cocked back, angled up-front, payload cradled at the tip
      px1(g, 10, 7, 'B');
      px1(g, 9, 6, 'B');
      px1(g, 8, 5, 'B');
      px1(g, 7, 4, 'B');
      px1(g, 6, 3, 'B');
      px1(g, 5, 2, 'B');
      rect(g, 3, 1, 2, 2, 'Y');
    } else {
      // arm swung down right after firing, spent cradle low, muzzle flash
      // where the payload just left
      px1(g, 10, 8, 'B');
      px1(g, 9, 8, 'B');
      px1(g, 8, 7, 'B');
      px1(g, 7, 7, 'B');
      rect(g, 5, 6, 2, 2, 'Y');
      px1(g, 3, 3, 'R');
    }
    autoOutline(g);
    return finalize(g);
  }
  return [frame(false), frame(true)];
}

function buildBurstWorkerFrames(): string[][] {
  // Small hovering orb, same family as the Inference Agent but tighter and
  // twitchier: a lightning-bolt motif zigzags above the body, with a couple
  // of speed-line dashes off to the sides. Frame 2 kicks the bolt and dashes
  // over a pixel so the whole thing reads as jittery/fast rather than a calm
  // blink like its cousin.
  function frame(kick: boolean): string[] {
    const g = makeGrid(U, U);
    const bx = kick ? 1 : 0;
    // lightning bolt, zigzagging above the orb
    px1(g, 8 + bx, 3, 'Y');
    px1(g, 7 + bx, 4, 'Y');
    px1(g, 8 + bx, 5, 'Y');
    px1(g, 7 + bx, 6, 'Y');
    // small orb body — tighter radius than the Inference Agent
    circle(g, 8, 9, 4, 'T');
    circle(g, 7, 8, 1, 't');
    // visor / chat glow
    circle(g, 8, 10, 1, 'K');
    px1(g, 8, 10, 'S');
    // speed lines, flicking side to side
    if (kick) {
      rect(g, 0, 8, 2, 1, 'S');
      rect(g, 1, 11, 2, 1, 'S');
    } else {
      rect(g, 0, 9, 2, 1, 'S');
      rect(g, 1, 6, 2, 1, 'S');
    }
    // thruster glow underneath (floating)
    rect(g, 6, 13, 4, 1, 'G');
    autoOutline(g);
    return finalize(g);
  }
  return [frame(false), frame(true)];
}

function buildReservedInstanceFrames(): string[][] {
  // Squat armored block-bot: low, wide chassis with flank armor plates and a
  // padlock/shield boss on the chest — the "prepaid, tanky" faction unit. A
  // slow blink on the visor is its only motion, matching the guardrail
  // sentinel's calm cadence.
  function frame(blink: boolean): string[] {
    const g = makeGrid(U, U);
    // short, thick legs
    rect(g, 5, 14, 2, 2, 'C');
    rect(g, 9, 14, 2, 2, 'C');
    // wide squat torso
    rect(g, 3, 8, 10, 6, 'T');
    rect(g, 4, 9, 3, 1, 't');
    rect(g, 10, 12, 2, 2, 'D');
    // flank armor plates
    rect(g, 1, 9, 2, 5, 'C');
    rect(g, 13, 9, 2, 5, 'C');
    // low, recessed head
    rect(g, 6, 6, 4, 2, 'B');
    rect(g, 7, 7, 2, 1, blink ? 'S' : 'K');
    // padlock/shield boss on the chest
    rect(g, 7, 8, 2, 1, 'A');
    px1(g, 7, 7, 'A');
    px1(g, 9, 7, 'A');
    rect(g, 6, 9, 4, 3, 'A');
    px1(g, 8, 10, 'Y');
    autoOutline(g);
    return finalize(g);
  }
  // heavy hold on the open-eye frame, brief blink — reads as slow
  return [frame(false), frame(false), frame(false), frame(true)];
}

function buildDistilledAgentFrames(): string[][] {
  // A smaller, plainer cousin of the Inference Agent: shorter antenna, a
  // tighter orb, and a dimmer glow throughout — visibly the "budget model."
  function frame(antennaOn: boolean): string[] {
    const g = makeGrid(U, U);
    // single short antenna — dimmer lit color than the Inference Agent's
    rect(g, 8, 4, 1, 1, 'B');
    px1(g, 8, 3, antennaOn ? 'S' : 'B');
    // small orb body
    circle(g, 8, 9, 4, 'T');
    circle(g, 7, 8, 1, 't');
    // visor / chat glow — dim
    circle(g, 8, 10, 1, 'K');
    px1(g, 8, 10, 'g');
    // faint thruster glow underneath (floating), narrower and dimmer
    rect(g, 7, 13, 2, 1, 'g');
    autoOutline(g);
    return finalize(g);
  }
  return [frame(true), frame(false)];
}

function buildSuperClusterFrames(): string[][] {
  // Hyperscale apex: a hulking armored datacenter-mech on stubby legs with a
  // pulsing power core and twin cannons. Reads big and slow.
  function frame(pulse: 0 | 1 | 2): string[] {
    const g = makeGrid(U, U);
    rect(g, 3, 12, 2, 3, 'C'); // legs
    rect(g, 11, 12, 2, 3, 'C');
    rect(g, 2, 4, 12, 9, 'T'); // broad chassis
    rect(g, 2, 4, 12, 1, 't');
    rect(g, 6, 4, 1, 9, 'C'); // armor seams
    rect(g, 9, 4, 1, 9, 'C');
    px1(g, 4, 6, 'G'); // rack LEDs
    px1(g, 4, 9, 'G');
    px1(g, 11, 6, 'G');
    px1(g, 11, 9, 'G');
    const core = pulse === 1 ? 'W' : 'S'; // pulsing power core
    circle(g, 8, 8, pulse === 1 ? 3 : 2, core);
    circle(g, 8, 8, 1, 'W');
    rect(g, 4, 2, 2, 2, 'C'); // twin cannons
    rect(g, 10, 2, 2, 2, 'C');
    autoOutline(g);
    return finalize(g);
  }
  return [frame(0), frame(1), frame(2)];
}

function buildSpotInstanceFrames(): string[][] {
  // Serverless raider: an arrowhead dart with a red combat tip and speed
  // lines — a burst-worker cousin with an edge.
  function frame(kick: boolean): string[] {
    const g = makeGrid(U, U);
    const bx = kick ? 1 : 0;
    diamond(g, 8 + bx, 8, 3, 4, 'T'); // dart body
    px1(g, 8 + bx, 5, 't');
    px1(g, 8 + bx, 4, 'R'); // red combat tip
    px1(g, 8 + bx, 8, 'K'); // visor
    px1(g, 8 + bx, 9, 'R');
    if (kick) {
      rect(g, 0, 7, 3, 1, 'S');
      rect(g, 1, 10, 2, 1, 'S');
    } else {
      rect(g, 0, 9, 3, 1, 'S');
      rect(g, 1, 6, 2, 1, 'S');
    }
    rect(g, 7, 12, 3, 1, 'G'); // thruster
    autoOutline(g);
    return finalize(g);
  }
  return [frame(false), frame(true)];
}

function buildForkNodeFrames(): string[][] {
  // Openweights swarm node: a tiny team-color node with a green fork/branch
  // motif sprouting above; the prong tips blink.
  function frame(on: boolean): string[] {
    const g = makeGrid(U, U);
    diamond(g, 8, 10, 3, 3, 'T'); // node body
    circle(g, 8, 10, 1, on ? 'G' : 'g');
    px1(g, 8, 6, 'C'); // fork stem
    px1(g, 8, 5, 'C');
    px1(g, 7, 5, 'C'); // branches
    px1(g, 6, 4, 'C');
    px1(g, 9, 5, 'C');
    px1(g, 10, 4, 'C');
    px1(g, 6, 3, on ? 'G' : 'g'); // prong tips
    px1(g, 10, 3, on ? 'G' : 'g');
    px1(g, 8, 4, on ? 'G' : 'g');
    autoOutline(g);
    return finalize(g);
  }
  return [frame(false), frame(true)];
}

const UNIT_BUILDERS: Record<UnitType, () => string[][]> = {
  engineer: buildEngineerFrames,
  inferenceAgent: buildInferenceAgentFrames,
  scoutCrawler: buildScoutCrawlerFrames,
  redTeamAgent: buildRedTeamAgentFrames,
  guardrailSentinel: buildGuardrailSentinelFrames,
  loadTester: buildLoadTesterFrames,
  frontierAgent: buildFrontierAgentFrames,
  burstWorker: buildBurstWorkerFrames,
  reservedInstance: buildReservedInstanceFrames,
  distilledAgent: buildDistilledAgentFrames,
  superCluster: buildSuperClusterFrames,
  spotInstance: buildSpotInstanceFrames,
  forkNode: buildForkNodeFrames,
};

// ---------------------------------------------------------------------------
// Building sprites (32x32 for size-2, 48x48 for size-3, 16x16 for size-1)
// ---------------------------------------------------------------------------

function buildHqFrames(): string[][] {
  function frame(dimIdx: number): string[] {
    const n = 48;
    const g = makeGrid(n, n);
    // foundation
    rect(g, 4, 42, 40, 4, 'C');
    // tower body (glass)
    rect(g, 8, 10, 32, 32, 'B');
    // vertical seams
    rect(g, 16, 10, 1, 32, 'C');
    rect(g, 24, 10, 1, 32, 'C');
    rect(g, 32, 10, 1, 32, 'C');
    // floor lines
    rect(g, 8, 18, 32, 1, 'C');
    rect(g, 8, 26, 32, 1, 'C');
    rect(g, 8, 34, 32, 1, 'C');
    // lit windows — one twinkles dark per frame, slow cadence
    const windows: Array<[number, number, number, number]> = [
      [10, 13, 5, 4],
      [27, 21, 5, 4],
      [18, 29, 5, 4],
      [33, 37, 4, 4],
    ];
    windows.forEach(([wx, wy, ww, wh], i) => {
      rect(g, wx, wy, ww, wh, i === dimIdx ? 'C' : 'S');
    });
    // roof / team banner band
    rect(g, 6, 6, 36, 4, 'T');
    rect(g, 6, 6, 36, 1, 't');
    // hanging team banner
    rect(g, 20, 42, 8, 6, 'T');
    rect(g, 22, 44, 4, 1, 't');
    // cloud logo (kept clear of the spire so it reads as a clean cloud, not a smudge)
    circle(g, 17, 7, 3, 'W');
    circle(g, 22, 5, 3, 'W');
    circle(g, 27, 7, 3, 'W');
    rect(g, 15, 7, 15, 3, 'W');
    // spire, poking up from the roof off to the side of the logo
    rect(g, 33, 1, 2, 5, 'C');
    px1(g, 34, 0, 'R');
    autoOutline(g);
    return finalize(g);
  }
  return [frame(3), frame(0), frame(1)];
}

function buildGpuClusterFrames(): string[][] {
  function frame(f: number): string[] {
    const n = 32;
    const g = makeGrid(n, n);
    rect(g, 2, 3, 28, 27, 'T');
    rect(g, 4, 5, 24, 23, 'C');
    // intake fans — hub glint alternates to suggest spin
    const hub = f % 2 === 0 ? 'S' : 'W';
    circle(g, 9, 9, 3, 'B');
    circle(g, 9, 9, 1, hub);
    circle(g, 23, 9, 3, 'B');
    circle(g, 23, 9, 1, hub);
    // LED rack rows — a lit block of 3-of-5 chases across each row
    for (const y of [14, 18, 22]) {
      rect(g, 6, y, 20, 2, 'K');
      for (let k = 0; k < 5; k++) {
        const lit = ((k - f + 5) % 5) < 3;
        px1(g, 8 + k * 4, y + 1, lit ? 'G' : 'g');
      }
    }
    // feet
    rect(g, 4, 28, 4, 2, 'C');
    rect(g, 24, 28, 4, 2, 'C');
    autoOutline(g);
    return finalize(g);
  }
  return [frame(0), frame(1), frame(2)];
}

function buildInferenceEndpoint(): string[] {
  const n = 32;
  const g = makeGrid(n, n);
  rect(g, 8, 28, 16, 3, 'C');
  rect(g, 13, 14, 6, 16, 'B');
  circle(g, 16, 10, 9, 'T');
  circle(g, 16, 10, 6, 'D');
  circle(g, 16, 10, 3, 'B');
  px1(g, 8, 10, 't');
  px1(g, 24, 10, 't');
  rect(g, 15, 2, 2, 8, 'B');
  circle(g, 16, 2, 2, 'S');
  autoOutline(g);
  return finalize(g);
}

function buildDataWarehouse(): string[] {
  const n = 32;
  const g = makeGrid(n, n);
  rect(g, 6, 6, 20, 22, 'T');
  ellipse(g, 16, 6, 10, 4, 't');
  ellipse(g, 16, 28, 10, 4, 'D');
  rect(g, 6, 12, 20, 1, 'D');
  rect(g, 6, 18, 20, 1, 'D');
  rect(g, 6, 24, 20, 1, 'D');
  rect(g, 13, 14, 6, 6, 'K');
  px1(g, 16, 17, 'S');
  autoOutline(g);
  return finalize(g);
}

function buildFinopsCenterFrames(): string[][] {
  const layouts: number[][] = [
    [3, 5, 2, 4],
    [5, 2, 4, 3],
    [2, 4, 3, 5],
  ];
  function frame(f: number): string[] {
    const n = 32;
    const g = makeGrid(n, n);
    rect(g, 4, 10, 24, 20, 'T');
    rect(g, 2, 8, 28, 2, 'D');
    rect(g, 7, 14, 6, 5, 'K');
    rect(g, 19, 14, 6, 5, 'K');
    rect(g, 14, 24, 4, 6, 'C');
    // holo-chart screen (dark panel so the bright bars read clearly)
    rect(g, 8, 1, 16, 7, 'K');
    rect(g, 8, 1, 16, 1, 'S');
    rect(g, 15, 8, 2, 2, 'C');
    const xs = [10, 13, 16, 19];
    const colors = ['G', 'W', 'G', 'W'];
    const heights = layouts[f];
    xs.forEach((x, i) => {
      const h = heights[i];
      rect(g, x, 8 - h, 2, h, colors[i]);
    });
    autoOutline(g);
    return finalize(g);
  }
  return [frame(0), frame(1), frame(2)];
}

function buildCacheLayerFrames(): string[][] {
  function frame(stage: 0 | 1 | 2): string[] {
    const n = 32;
    const g = makeGrid(n, n);
    rect(g, 8, 27, 16, 3, 'C');
    diamond(g, 7, 15, 4, 5, 'D');
    diamond(g, 25, 16, 4, 5, 'D');
    diamond(g, 16, 20, 10, 10, 'T');
    // breathing inner glow
    if (stage === 0) {
      diamond(g, 16, 20, 6, 6, 'S');
      diamond(g, 16, 20, 2, 2, 'W');
    } else if (stage === 1) {
      diamond(g, 16, 20, 7, 7, 'S');
      diamond(g, 16, 20, 3, 3, 'W');
    } else {
      diamond(g, 16, 20, 5, 5, 'S');
      diamond(g, 16, 20, 2, 2, 'W');
    }
    autoOutline(g);
    return finalize(g);
  }
  return [frame(0), frame(1), frame(2)];
}

function buildFirewallFrames(): string[][] {
  function frame(stage: 0 | 1 | 2): string[] {
    const n = 16;
    const g = makeGrid(n, n);
    // wide tapered base
    rect(g, 4, 12, 8, 3, 'C');
    rect(g, 5, 9, 6, 4, 'C');
    // team-color rectangular plaque (reads as a banner, not a body)
    rect(g, 6, 10, 4, 3, 'T');
    rect(g, 6, 10, 4, 1, 't');
    // platform with crenellations
    rect(g, 2, 7, 12, 2, 'B');
    px1(g, 2, 6, 'B');
    px1(g, 5, 6, 'B');
    px1(g, 10, 6, 'B');
    px1(g, 13, 6, 'B');
    // brazier pole + flame — flickers between 3 small variants
    rect(g, 7, 4, 2, 3, 'C');
    if (stage === 0) {
      circle(g, 8, 2, 2, 'R');
      circle(g, 8, 2, 1, 'Y');
    } else if (stage === 1) {
      circle(g, 8, 1, 2, 'Y');
      circle(g, 8, 2, 1, 'R');
    } else {
      circle(g, 8, 2, 2, 'Y');
      circle(g, 8, 2, 1, 'R');
    }
    autoOutline(g);
    return finalize(g);
  }
  return [frame(0), frame(1), frame(2)];
}

function buildTrainingClusterFrames(): string[][] {
  function frame(stage: 0 | 1 | 2): string[] {
    const n = 48;
    const g = makeGrid(n, n);
    rect(g, 4, 8, 40, 36, 'T');
    rect(g, 2, 5, 44, 4, 'D');
    rect(g, 6, 14, 3, 20, 'C');
    rect(g, 39, 14, 3, 20, 'C');
    // small hull vents for surface texture
    rect(g, 10, 12, 2, 2, 'D');
    rect(g, 10, 34, 2, 2, 'D');
    rect(g, 36, 12, 2, 2, 'D');
    rect(g, 36, 34, 2, 2, 'D');
    circle(g, 24, 26, 14, 'C');
    circle(g, 24, 26, 10, 'K');
    // reactor core pulse
    if (stage === 0) {
      circle(g, 24, 26, 6, 'W');
      circle(g, 24, 26, 3, 'S');
    } else if (stage === 1) {
      circle(g, 24, 26, 7, 'S');
      circle(g, 24, 26, 3, 'W');
    } else {
      circle(g, 24, 26, 5, 'W');
      circle(g, 24, 26, 2, 'S');
    }
    rect(g, 4, 40, 40, 2, 'Y');
    autoOutline(g);
    return finalize(g);
  }
  return [frame(0), frame(1), frame(2)];
}

function buildSubstationFrames(): string[][] {
  // transformer yard: concrete pad, two coil stacks, crossbar pylon,
  // 2-frame electric arc between the coils
  function frame(arc: boolean): string[] {
    const n = 32;
    const g = makeGrid(n, n);
    rect(g, 3, 24, 26, 5, 'D'); // pad
    rect(g, 3, 24, 26, 1, 'K');
    // coil stacks
    for (const cx of [9, 23]) {
      rect(g, cx - 3, 12, 6, 12, 'T');
      rect(g, cx - 3, 14, 6, 1, 'D');
      rect(g, cx - 3, 17, 6, 1, 'D');
      rect(g, cx - 3, 20, 6, 1, 'D');
      rect(g, cx - 1, 8, 2, 4, 't'); // bushing
    }
    // crossbar pylon
    rect(g, 6, 6, 20, 2, 'K');
    rect(g, 15, 2, 2, 22, 'K');
    px1(g, 16, 2, 'S');
    // warning stripe
    rect(g, 3, 28, 26, 1, 'Y');
    // arc between bushings
    if (arc) {
      px1(g, 12, 9, 'W');
      px1(g, 14, 10, 'S');
      px1(g, 17, 9, 'W');
      px1(g, 19, 10, 'S');
    } else {
      px1(g, 15, 9, 'S');
    }
    autoOutline(g);
    return finalize(g);
  }
  return [frame(false), frame(true)];
}

function buildSyntheticDataFrames(): string[][] {
  // Distillery/reactor vat: team-color body with a glass window onto a teal
  // (data-colored) liquid pool, piped into a small secondary tank. Bubbles
  // climb through the pool across the 3 frames, breaking the surface on the
  // last one, then reset.
  function frame(stage: 0 | 1 | 2): string[] {
    const n = 32;
    const g = makeGrid(n, n);
    // foundation pad
    rect(g, 2, 27, 28, 3, 'C');
    // main vat (reactor body)
    rect(g, 6, 8, 14, 19, 'T');
    ellipse(g, 13, 8, 7, 3, 't');
    ellipse(g, 13, 27, 7, 3, 'D');
    // glass window onto the distillate
    rect(g, 8, 12, 10, 13, 'K');
    rect(g, 9, 17, 8, 7, 'Q');
    ellipse(g, 13, 17, 4, 1, 'q');
    // rising bubbles — position climbs each frame, breaking the surface on the last
    const bubbleYs = stage === 0 ? [19, 21] : stage === 1 ? [15, 18] : [12, 15];
    px1(g, 11, bubbleYs[0], 'q');
    px1(g, 15, bubbleYs[1], 'q');
    if (stage === 2) px1(g, 13, 13, 'W'); // bubble breaking the surface
    // pipe running to the small secondary tank
    rect(g, 20, 19, 4, 2, 'C');
    // secondary tank
    rect(g, 24, 14, 6, 13, 'B');
    ellipse(g, 27, 14, 3, 2, 'A');
    rect(g, 25, 22, 4, 4, 'Q');
    // rooftop vent stack
    rect(g, 11, 3, 3, 5, 'C');
    px1(g, 12, 2, stage === 1 ? 'A' : 'B');
    autoOutline(g);
    return finalize(g);
  }
  return [frame(0), frame(1), frame(2)];
}

function buildAdversarialLabFrames(): string[][] {
  // Black-hat intrusion rig: dark server face on a team-colored chassis, with
  // a red breach glyph that pulses across the 3 frames.
  function frame(stage: 0 | 1 | 2): string[] {
    const n = 32;
    const g = makeGrid(n, n);
    rect(g, 3, 26, 26, 3, 'C'); // foundation pad
    rect(g, 5, 8, 22, 18, 'T'); // chassis
    rect(g, 5, 8, 22, 2, 't'); // lit top edge
    rect(g, 8, 12, 16, 11, 'K'); // dark server face
    // rack status LEDs (mostly hostile red)
    for (const ry of [13, 16, 19]) {
      px1(g, 10, ry, 'R');
      px1(g, 12, ry, stage === 1 ? 'R' : 'M');
    }
    // pulsing intrusion glyph
    const glow = stage === 0 ? 'M' : stage === 1 ? 'R' : 'W';
    diamond(g, 19, 17, 4, 4, glow);
    diamond(g, 19, 17, 2, 2, 'K');
    px1(g, 18, 16, 'R');
    px1(g, 20, 16, 'R');
    // antenna with blinking tip
    rect(g, 15, 4, 2, 4, 'C');
    px1(g, 15, 3, stage === 2 ? 'R' : 'M');
    autoOutline(g);
    return finalize(g);
  }
  return [frame(0), frame(1), frame(2)];
}

function buildSocCenterFrames(): string[][] {
  // Blue-team NOC: a bright shield emblem on a monitor wall, with a green
  // scan line sweeping left-to-right across the 3 frames.
  function frame(stage: 0 | 1 | 2): string[] {
    const n = 32;
    const g = makeGrid(n, n);
    rect(g, 2, 26, 28, 3, 'C'); // foundation pad
    rect(g, 5, 9, 22, 17, 'T'); // bunker body
    rect(g, 5, 9, 22, 2, 't'); // lit top edge
    rect(g, 8, 12, 16, 9, 'K'); // monitor wall (dark glass)
    // scanning sweep line advances each frame
    const sx = 9 + stage * 5;
    rect(g, sx, 12, 1, 9, 'G');
    // shield emblem
    diamond(g, 16, 16, 5, 6, 'S');
    diamond(g, 16, 16, 3, 4, 'W');
    px1(g, 16, 15, 'G');
    px1(g, 15, 16, 'G');
    px1(g, 17, 17, 'G');
    // rooftop dish
    rect(g, 12, 5, 8, 2, 'C');
    px1(g, 16, 4, stage === 1 ? 'S' : 'B');
    autoOutline(g);
    return finalize(g);
  }
  return [frame(0), frame(1), frame(2)];
}

const BUILDING_BUILDERS: Record<BuildingType, () => string[][]> = {
  hq: buildHqFrames,
  substation: buildSubstationFrames,
  gpuCluster: buildGpuClusterFrames,
  inferenceEndpoint: () => [buildInferenceEndpoint()],
  dataWarehouse: () => [buildDataWarehouse()],
  finopsCenter: buildFinopsCenterFrames,
  cacheLayer: buildCacheLayerFrames,
  firewall: buildFirewallFrames,
  trainingCluster: buildTrainingClusterFrames,
  syntheticData: buildSyntheticDataFrames,
  adversarialLab: buildAdversarialLabFrames,
  socCenter: buildSocCenterFrames,
};

// ---------------------------------------------------------------------------
// Neutral sprites
// ---------------------------------------------------------------------------

function buildDataNodeFrames(): string[][] {
  function frame(sparkle: boolean): string[] {
    const g = makeGrid(U, U);
    diamond(g, 3, 11, 2, 3, 'Q');
    diamond(g, 8, 8, 6, 7, 'Q');
    diamond(g, 8, 8, 3, 4, 'q');
    px1(g, 8, 2, 'W');
    if (sparkle) {
      px1(g, 12, 4, 'W');
      px1(g, 4, 5, 'W');
    }
    autoOutline(g);
    return finalize(g);
  }
  // mostly-static with one occasional sparkle frame in the cycle
  return [frame(false), frame(false), frame(false), frame(false), frame(false), frame(true)];
}

function buildMarketFrames(): string[][] {
  function full(mirrored: boolean): string[] {
    const g = makeGrid(U, U);
    circle(g, 8, 8, 7, 'Y');
    cut(g, 8, 8, 4);
    circle(g, 8, 8, 2, 'Y');
    circle(g, mirrored ? 9 : 7, 7, 1, 'W');
    autoOutline(g);
    return finalize(g);
  }
  function edge(): string[] {
    const g = makeGrid(U, U);
    rect(g, 7, 2, 2, 12, 'Y');
    rect(g, 7, 6, 2, 4, 'W');
    autoOutline(g);
    return finalize(g);
  }
  // full -> edge -> full mirrored -> edge (a coin rotating in place)
  return [full(false), edge(), full(true), edge()];
}

// ---------------------------------------------------------------------------
// Frame cadence — how sprites pick their current frame
// ---------------------------------------------------------------------------

export interface FrameCadence {
  frames: number;
  msPerFrame: number;
}

/** Units whose sprite continuously cycles frames; engineer/scoutCrawler are gated to movement by the renderer. */
export const UNIT_FRAME_CADENCE: Partial<Record<UnitType, FrameCadence>> = {
  engineer: { frames: 2, msPerFrame: 220 },
  inferenceAgent: { frames: 2, msPerFrame: 900 },
  scoutCrawler: { frames: 2, msPerFrame: 150 },
  redTeamAgent: { frames: 2, msPerFrame: 260 },
  guardrailSentinel: { frames: 4, msPerFrame: 450 },
  loadTester: { frames: 2, msPerFrame: 300 }, // arm cock/recoil
  frontierAgent: { frames: 3, msPerFrame: 200 },
  burstWorker: { frames: 2, msPerFrame: 400 }, // jittery bolt kick
  reservedInstance: { frames: 4, msPerFrame: 900 }, // slow blink
  distilledAgent: { frames: 2, msPerFrame: 700 }, // dim antenna blink
  superCluster: { frames: 3, msPerFrame: 260 }, // core pulse
  spotInstance: { frames: 2, msPerFrame: 220 }, // twitchy dart
  forkNode: { frames: 2, msPerFrame: 600 }, // prong blink
};

export const BUILDING_FRAME_CADENCE: Partial<Record<BuildingType, FrameCadence>> = {
  gpuCluster: { frames: 3, msPerFrame: 130 }, // fans/LEDs — fast
  substation: { frames: 2, msPerFrame: 340 }, // electric arc crackle
  finopsCenter: { frames: 3, msPerFrame: 600 },
  cacheLayer: { frames: 3, msPerFrame: 500 },
  trainingCluster: { frames: 3, msPerFrame: 250 },
  hq: { frames: 3, msPerFrame: 800 }, // window twinkle — slow
  firewall: { frames: 3, msPerFrame: 220 },
  syntheticData: { frames: 3, msPerFrame: 400 }, // bubbles rising
};

export const DATA_NODE_CADENCE: FrameCadence = { frames: 6, msPerFrame: 300 };
export const MARKET_CADENCE: FrameCadence = { frames: 4, msPerFrame: 180 };

/** De-synced frame index: identical sprites drift apart because entityId offsets the clock. */
export function frameIndexFor(cadence: FrameCadence, entityId: number, now: number): number {
  return Math.floor(now / cadence.msPerFrame + entityId) % cadence.frames;
}

// ---------------------------------------------------------------------------
// Atlas
// ---------------------------------------------------------------------------

const GHOST_OK = '#46e678';
const GHOST_BAD = '#e64646';

class SpriteAtlas {
  private unitCache = new Map<string, HTMLCanvasElement>();
  private buildingCache = new Map<string, HTMLCanvasElement>();
  private ghostCache = new Map<string, HTMLCanvasElement>();
  private neutralCache = new Map<string, HTMLCanvasElement>();

  private unitGrids = new Map<UnitType, string[][]>();
  private buildingGrids = new Map<BuildingType, string[][]>();
  private dataNodeGrids: string[][];
  private marketGrids: string[][];

  constructor() {
    for (const key of Object.keys(UNIT_BUILDERS) as UnitType[]) {
      this.unitGrids.set(key, UNIT_BUILDERS[key]());
    }
    for (const key of Object.keys(BUILDING_BUILDERS) as BuildingType[]) {
      this.buildingGrids.set(key, BUILDING_BUILDERS[key]());
    }
    this.dataNodeGrids = buildDataNodeFrames();
    this.marketGrids = buildMarketFrames();
  }

  private teamColorFor(owner: number): string {
    if (owner < 0 || owner >= PLAYER_COLORS.length) return NEUTRAL_TEAM_COLOR;
    return PLAYER_COLORS[owner];
  }

  unitFrameCount(type: UnitType): number {
    return this.unitGrids.get(type)!.length;
  }

  buildingFrameCount(type: BuildingType): number {
    return this.buildingGrids.get(type)!.length;
  }

  getUnit(type: UnitType, owner: number, frame = 0, flip = false): HTMLCanvasElement {
    const grids = this.unitGrids.get(type)!;
    const f = ((frame % grids.length) + grids.length) % grids.length;
    const key = `${type}:${owner}:${f}:${flip ? 1 : 0}`;
    let c = this.unitCache.get(key);
    if (!c) {
      const baseKey = `${type}:${owner}:${f}:0`;
      let base = this.unitCache.get(baseKey);
      if (!base) {
        base = bake(grids[f], { ...NEUTRAL, ...teamPalette(this.teamColorFor(owner)) });
        this.unitCache.set(baseKey, base);
      }
      c = flip ? mirrorH(base) : base;
      this.unitCache.set(key, c);
    }
    return c;
  }

  getBuilding(type: BuildingType, owner: number, frame = 0): HTMLCanvasElement {
    const grids = this.buildingGrids.get(type)!;
    const f = ((frame % grids.length) + grids.length) % grids.length;
    const key = `${type}:${owner}:${f}`;
    let c = this.buildingCache.get(key);
    if (!c) {
      c = bake(grids[f], { ...NEUTRAL, ...teamPalette(this.teamColorFor(owner)) });
      this.buildingCache.set(key, c);
    }
    return c;
  }

  getGhostBuilding(type: BuildingType, ok: boolean): HTMLCanvasElement {
    const key = `${type}:${ok ? 'ok' : 'bad'}`;
    let c = this.ghostCache.get(key);
    if (!c) {
      const base = this.getBuilding(type, 0, 0);
      c = silhouette(base, ok ? GHOST_OK : GHOST_BAD);
      this.ghostCache.set(key, c);
    }
    return c;
  }

  getDataNode(frame = 0): HTMLCanvasElement {
    const f = ((frame % this.dataNodeGrids.length) + this.dataNodeGrids.length) % this.dataNodeGrids.length;
    const key = `dataNode:${f}`;
    let c = this.neutralCache.get(key);
    if (!c) {
      c = bake(this.dataNodeGrids[f], NEUTRAL);
      this.neutralCache.set(key, c);
    }
    return c;
  }

  getMarket(frame = 0): HTMLCanvasElement {
    const f = ((frame % this.marketGrids.length) + this.marketGrids.length) % this.marketGrids.length;
    const key = `market:${f}`;
    let c = this.neutralCache.get(key);
    if (!c) {
      c = bake(this.marketGrids[f], NEUTRAL);
      this.neutralCache.set(key, c);
    }
    return c;
  }
}

export const SPRITES = new SpriteAtlas();
